import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Card, CardHeader, CardBody, ListGroup, ListGroupItem, Button } from "reactstrap";
import ReactApexChart from "react-apexcharts";

function StudentProgressChartsModal({
  isOpen,
  onClose,
  charts = {},
  studentName = "Aluno"
}) {
  if (!isOpen) return null;

  const classroomTime = charts.classroomTime || [];
  const classroomLessons = charts.classroomLessons || [];
  const unitTime = charts.unitTime || [];
  const lessonTypes = charts.lessonTypes || [];
  const hasAnyChartData = classroomTime.length || classroomLessons.length || unitTime.length || lessonTypes.length;

  const minutesSeriesFromSeconds = list => list.map(item => Math.max(0, Math.round(Number(item.totalSeconds || 0) / 60)));
  const numericFormatter = value => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return `${Math.round(parsed)} min`;
  };
  const durationFormatter = value => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return "0m";
    const sec = Math.round(parsed) * 60;
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    return parts.join(" ") || "0m";
  };

  const classroomTimeOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: false },
    xaxis: { categories: classroomTime.map(item => item.name), labels: { formatter: numericFormatter } },
    tooltip: { y: { formatter: durationFormatter } },
    theme: { mode: "light" }
  };
  const classroomTimeSeries = [{ name: "Tempo (min)", data: minutesSeriesFromSeconds(classroomTime) }];

  const classroomLessonsOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: false },
    xaxis: { categories: classroomLessons.map(item => item.name), labels: { formatter: value => {
      const parsed = Number(value);
      if (Number.isNaN(parsed)) return value;
      return `${Math.round(parsed)}`;
    } } },
    tooltip: { y: { formatter: value => `${Math.round(Number(value) || 0)} aulas` } }
  };
  const classroomLessonsSeries = [{ name: "Quantidade de aulas", data: classroomLessons.map(item => Number(item.lessons) || 0) }];

  const unitTimeOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: false },
    xaxis: { categories: unitTime.map(item => item.name), labels: { formatter: numericFormatter } },
    tooltip: { y: { formatter: durationFormatter } }
  };
  const unitTimeSeries = [{ name: "Tempo (min)", data: minutesSeriesFromSeconds(unitTime) }];

  const lessonTypeOptions = {
    labels: lessonTypes.map(item => item.label),
    legend: { position: "bottom" },
    tooltip: { y: { formatter: value => `${Math.round(Number(value) || 0)} aulas` } }
  };
  const lessonTypeSeries = lessonTypes.map(item => Number(item.count) || 0);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className="modal-dialog-scrollable" style={{ maxWidth: "95%", maxHeight: "85vh" }}>
      <ModalHeader toggle={onClose}>Visão em gráficos — {studentName}</ModalHeader>
      <ModalBody>
        {hasAnyChartData ? (
          <Row>
            <Col lg="6" className="mb-4">
              <Card className="shadow-sm h-100">
                <CardHeader className="bg-transparent border-0 pb-0">
                  <h5 className="mb-0">Tempo por turma</h5>
                  <div className="text-sm text-muted">Top 15 turmas por tempo total (minutos)</div>
                </CardHeader>
                <CardBody>
                  {classroomTime.length ? (
                    <ReactApexChart options={classroomTimeOptions} series={classroomTimeSeries} type="bar" height={classroomTime.length * 36 + 80} />
                  ) : (
                    <div className="text-center text-muted py-4">Dados insuficientes para este gráfico.</div>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg="6" className="mb-4">
              <Card className="shadow-sm h-100">
                <CardHeader className="bg-transparent border-0 pb-0">
                  <h5 className="mb-0">Aulas concluídas por turma</h5>
                  <div className="text-sm text-muted">Top 15 turmas por quantidade de aulas concluídas</div>
                </CardHeader>
                <CardBody>
                  {classroomLessons.length ? (
                    <ReactApexChart options={classroomLessonsOptions} series={classroomLessonsSeries} type="bar" height={classroomLessons.length * 36 + 80} />
                  ) : (
                    <div className="text-center text-muted py-4">Dados insuficientes para este gráfico.</div>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg="6" className="mb-4">
              <Card className="shadow-sm h-100">
                <CardHeader className="bg-transparent border-0 pb-0">
                  <h5 className="mb-0">Tempo consolidado por unidade</h5>
                  <div className="text-sm text-muted">Top 15 unidades por tempo total (minutos)</div>
                </CardHeader>
                <CardBody>
                  {unitTime.length ? (
                    <ReactApexChart options={unitTimeOptions} series={unitTimeSeries} type="bar" height={unitTime.length * 36 + 80} />
                  ) : (
                    <div className="text-center text-muted py-4">Dados insuficientes para este gráfico.</div>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg="6" className="mb-4">
              <Card className="shadow-sm h-100">
                <CardHeader className="bg-transparent border-0 pb-0">
                  <h5 className="mb-0">Distribuição por tipo de aula</h5>
                  <div className="text-sm text-muted">Quantidade total de aulas concluídas por tipo</div>
                </CardHeader>
                <CardBody className="d-flex flex-column align-items-center">
                  {lessonTypes.length ? (
                    <>
                      <ReactApexChart options={lessonTypeOptions} series={lessonTypeSeries} type="donut" height={320} />
                      <ListGroup flush className="w-100 mt-3">
                        {lessonTypes.map(item => (
                          <ListGroupItem key={item.label} className="d-flex justify-content-between align-items-center">
                            <span>{item.label}</span>
                            <span className="text-sm text-muted">{item.count} aulas · {(() => { const sec = Number(item.totalSeconds || 0); const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60); const parts = []; if (h > 0) parts.push(`${h}h`); if (m > 0) parts.push(`${m}m`); return parts.join(" ") || "0m"; })()}</span>
                          </ListGroupItem>
                        ))}
                      </ListGroup>
                    </>
                  ) : (
                    <div className="text-center text-muted py-4">Dados insuficientes para este gráfico.</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          <div className="text-center text-muted py-5">Não há dados suficientes para montar os gráficos deste aluno.</div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Fechar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default StudentProgressChartsModal;
